import { useCallback, useEffect, useState } from 'react';
import { WizardAdmissionStep } from './WizardAdmissionStep';
import type { AdmissionMode } from '../../lib/admissions';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  createEvent, updateEvent, createTier, deleteTier, getManagedEvent,
  type EventDraftInput, type ManagedTier,
} from '../../lib/organizer';
import { ApiError } from '../../lib/apiClient';
import { formatMoney } from '../../lib/format';
import { useEventPaths } from '../../admin/routeBase';
import { usePageChrome } from '../../admin/pageChrome';
import './OrganizerPages.css';

/**
 * Event creation wizard.
 *
 * Progress is committed to the ENGINE on every Continue, not held in component
 * state until a final submit. Step 1 creates the draft row and returns an id;
 * every later step writes against that id. So closing the tab, losing the
 * network, or navigating away costs at most the step in progress — the draft is
 * already on the server and shows up in the dashboard.
 *
 * The URL carries the draft id from step 2 onward, which also makes the
 * half-finished wizard resumable by going back to it.
 */

// Step 2 covers HOW people get in, which for most events means tickets and tiers and for
// some means an RSVP or a request. Renamed rather than split: a fifth step would have
// renumbered the whole wizard and given a ticketed event a screen it has no use for (§88).
const STEPS = ['Basics', 'Admissions', 'Media', 'Review'] as const;

const AGE_OPTIONS = ['All ages', '16+', '18+', '21+'];
const CATEGORY_OPTIONS = ['concert', 'festival', 'club night', 'showcase', 'other'];

type Basics = {
  title: string; description: string;
  venueName: string; venueAddress: string; venueCity: string; venueState: string;
  eventDate: string; eventTime: string; doorsTime: string;
  ageRestriction: string; genre: string; category: string;
  expectedAttendance: string;
};

const EMPTY_BASICS: Basics = {
  title: '', description: '',
  venueName: '', venueAddress: '', venueCity: '', venueState: '',
  eventDate: '', eventTime: '', doorsTime: '',
  ageRestriction: '', genre: '', category: '',
  expectedAttendance: '',
};

/** Dollars in the form, integer cents on the wire. */
const toCents = (dollars: string) => Math.round(Number(dollars) * 100);

function toPayload(b: Basics): EventDraftInput {
  const attendance = Number.parseInt(b.expectedAttendance, 10);
  return {
    title: b.title.trim(),
    description: b.description.trim() || null,
    venueName: b.venueName.trim() || null,
    venueAddress: b.venueAddress.trim() || null,
    venueCity: b.venueCity.trim() || null,
    venueState: b.venueState.trim() || null,
    eventDate: b.eventDate || null,
    eventTime: b.eventTime || null,
    doorsTime: b.doorsTime || null,
    ageRestriction: b.ageRestriction || null,
    genre: b.genre.trim() || null,
    category: b.category || null,
    expectedAttendance: Number.isFinite(attendance) ? attendance : null,
  };
}

export function EventWizard() {
  const navigate = useNavigate();
  const { eventId: paramId } = useParams<{ eventId: string }>();
  // Where this wizard is mounted. Under /organizer it answers exactly as it always did.
  const paths = useEventPaths();

  const [step, setStep] = useState(0);
  // Which admission settings and which step-2 body to show. Defaults to the behaviour every
  // event had before admissions existed.
  const [admissionMode, setAdmissionMode] = useState<AdmissionMode>('ticketed');
  const [eventId, setEventId] = useState<string | null>(paramId ?? null);
  const [basics, setBasics] = useState<Basics>(EMPTY_BASICS);
  const [tiers, setTiers] = useState<ManagedTier[]>([]);
  const [imageUrl, setImageUrl] = useState('');
  // Optional. Empty is the normal case and saves as null.
  const [videoUrl, setVideoUrl] = useState('');
  const [videoWebmUrl, setVideoWebmUrl] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(paramId));

  // Declared unconditionally: the hook is a no-op wherever there is no chrome channel,
  // which is how a shared component avoids an `if (isAdmin)` inside it. No title is
  // published, because the wizard renders its own heading and the shell would repeat it.
  usePageChrome({
    breadcrumb: [
      { label: 'Admin', to: '/admin' },
      { label: 'Events', to: paths.list },
      { label: eventId ? 'Edit draft' : 'Create event' },
    ],
  }, [paths.list, eventId]);

  // Resuming a half-finished draft: pull whatever is already saved.
  useEffect(() => {
    if (!paramId) return;
    let cancelled = false;
    getManagedEvent(paramId)
      .then((e) => {
        if (cancelled) return;
        setBasics({
          title: e.title ?? '',
          description: e.description ?? '',
          venueName: e.venueName ?? '',
          venueAddress: e.venueAddress ?? '',
          venueCity: e.venueCity ?? '',
          venueState: e.venueState ?? '',
          eventDate: e.eventDate ? String(e.eventDate).slice(0, 10) : '',
          eventTime: e.eventTime ? String(e.eventTime).slice(0, 5) : '',
          doorsTime: e.doorsTime ? String(e.doorsTime).slice(0, 5) : '',
          ageRestriction: e.ageRestriction ?? '',
          genre: e.genre ?? '',
          category: e.category ?? '',
          expectedAttendance: e.expectedAttendance != null ? String(e.expectedAttendance) : '',
        });
        setTiers(e.tiers);
        setImageUrl(e.featuredImageUrl ?? '');
        setVideoUrl(e.featuredVideoUrl ?? '');
        setVideoWebmUrl(e.featuredVideoWebmUrl ?? '');
        setStep(1);
      })
      .catch(() => { if (!cancelled) setError('Could not load that draft.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [paramId]);

  const refreshTiers = useCallback(async (id: string) => {
    const e = await getManagedEvent(id);
    setTiers(e.tiers);
  }, []);

  /** Step 1 Continue: create the draft (or save edits to it) server-side. */
  async function commitBasics() {
    if (!basics.title.trim()) {
      setError('A title is required to save the draft.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (eventId) {
        await updateEvent(eventId, toPayload(basics));
      } else {
        const created = await createEvent(toPayload(basics));
        setEventId(created.id);
        // Put the id in the URL so a reload resumes rather than starting over.
        // paths.draft, not a literal: under the admin shell this must stay inside /admin,
        // or the push unmounts the console mid-wizard.
        navigate(paths.draft(created.id), { replace: true });
      }
      setStep(1);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not save the draft.');
    } finally {
      setSaving(false);
    }
  }

  async function commitImage() {
    if (!eventId) return;
    setSaving(true);
    setError(null);
    try {
      await updateEvent(eventId, {
        featuredImageUrl: imageUrl.trim() || null,
        featuredVideoUrl: videoUrl.trim() || null,
        featuredVideoWebmUrl: videoWebmUrl.trim() || null,
      });
      setStep(3);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not save the image.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="org-status">Loading draft…</p>;

  return (
    <div className="org-page org-wizard">
      <header className="org-header">
        <div>
          <h1 className="org-title display">
            {eventId ? 'Edit draft' : 'Create event'}
          </h1>
          <p className="org-lede">
            Saved as you go, your draft is stored after each step, not just at the end.
          </p>
        </div>
        <Link to={paths.list} className="button button--ghost">Exit</Link>
      </header>

      <ol className="wiz-steps">
        {STEPS.map((label, i) => (
          <li
            key={label}
            className={`wiz-step${i === step ? ' is-current' : ''}${i < step ? ' is-done' : ''}`}
          >
            <button
              type="button"
              className="wiz-step__button"
              // Earlier steps are revisitable once the draft exists on the server.
              disabled={!eventId || i > step}
              onClick={() => setStep(i)}
            >
              <span className="wiz-step__num mono">{i < step ? '✓' : i + 1}</span>
              <span className="wiz-step__label">{label}</span>
            </button>
          </li>
        ))}
      </ol>

      {error && <p className="org-error" role="alert">{error}</p>}

      {/* ---------------------------------------------------------------- 1 */}
      {step === 0 && (
        <section className="wiz-panel">
          <h2 className="wiz-panel__title">Basic information</h2>

          <div className="wiz-grid">
            <label className="field wiz-field--wide">
              <span className="field__label">Event title *</span>
              <input
                className="field__input" type="text" required value={basics.title}
                placeholder="Nocturne: VELA"
                onChange={(e) => setBasics((b) => ({ ...b, title: e.target.value }))}
              />
            </label>

            <label className="field wiz-field--wide">
              <span className="field__label">Description</span>
              <textarea
                className="field__input wiz-textarea" rows={4} value={basics.description}
                placeholder="What the night is, who's playing, what to expect."
                onChange={(e) => setBasics((b) => ({ ...b, description: e.target.value }))}
              />
            </label>

            <label className="field">
              <span className="field__label">Venue name</span>
              <input className="field__input" type="text" value={basics.venueName}
                onChange={(e) => setBasics((b) => ({ ...b, venueName: e.target.value }))} />
            </label>
            <label className="field">
              <span className="field__label">Address</span>
              <input className="field__input" type="text" value={basics.venueAddress}
                onChange={(e) => setBasics((b) => ({ ...b, venueAddress: e.target.value }))} />
            </label>
            <label className="field">
              <span className="field__label">City</span>
              <input className="field__input" type="text" value={basics.venueCity}
                onChange={(e) => setBasics((b) => ({ ...b, venueCity: e.target.value }))} />
            </label>
            <label className="field">
              <span className="field__label">State</span>
              <input className="field__input" type="text" maxLength={2} value={basics.venueState}
                placeholder="CO"
                onChange={(e) => setBasics((b) => ({ ...b, venueState: e.target.value.toUpperCase() }))} />
            </label>

            <label className="field">
              <span className="field__label">Event date</span>
              <input className="field__input" type="date" value={basics.eventDate}
                onChange={(e) => setBasics((b) => ({ ...b, eventDate: e.target.value }))} />
            </label>
            <label className="field">
              <span className="field__label">Start time</span>
              <input className="field__input" type="time" value={basics.eventTime}
                onChange={(e) => setBasics((b) => ({ ...b, eventTime: e.target.value }))} />
            </label>
            <label className="field">
              <span className="field__label">Doors</span>
              <input className="field__input" type="time" value={basics.doorsTime}
                onChange={(e) => setBasics((b) => ({ ...b, doorsTime: e.target.value }))} />
            </label>

            <label className="field">
              <span className="field__label">Age restriction</span>
              <select className="field__input" value={basics.ageRestriction}
                onChange={(e) => setBasics((b) => ({ ...b, ageRestriction: e.target.value }))}>
                <option value="">Not specified</option>
                {AGE_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
            <label className="field">
              <span className="field__label">Genre</span>
              <input className="field__input" type="text" value={basics.genre} placeholder="techno"
                onChange={(e) => setBasics((b) => ({ ...b, genre: e.target.value }))} />
            </label>
            <label className="field">
              <span className="field__label">Category</span>
              <select className="field__input" value={basics.category}
                onChange={(e) => setBasics((b) => ({ ...b, category: e.target.value }))}>
                <option value="">Not specified</option>
                {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="field">
              <span className="field__label">Expected attendance</span>
              <input className="field__input mono" type="number" min="0" value={basics.expectedAttendance}
                onChange={(e) => setBasics((b) => ({ ...b, expectedAttendance: e.target.value }))} />
            </label>
          </div>

          <div className="wiz-actions">
            <span className="org-hint">Saving creates the draft, nothing goes on sale.</span>
            <button type="button" className="button button--primary"
              disabled={saving || !basics.title.trim()} onClick={commitBasics}>
              {saving ? 'Saving…' : 'Save & continue'}
            </button>
          </div>
        </section>
      )}

      {/* ---------------------------------------------------------------- 2 */}
      {step === 1 && eventId && (
        <section className="wiz-panel">
          <h2 className="wiz-panel__title">Admissions</h2>
          <WizardAdmissionStep eventId={eventId} onModeChange={setAdmissionMode} />

          {/* Tiers belong to a ticketed event and to nothing else (§89). */}
          {admissionMode === 'ticketed' ? (
            <TierStep
              eventId={eventId}
              tiers={tiers}
              onChanged={() => refreshTiers(eventId)}
              onBack={() => setStep(0)}
              onNext={() => setStep(2)}
            />
          ) : (
            <div className="wiz-actions">
              <button type="button" className="button button--ghost" onClick={() => setStep(0)}>
                Back
              </button>
              <button type="button" className="button button--primary" onClick={() => setStep(2)}>
                Continue
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------------------------------------------------------- 3 */}
      {step === 2 && (
        <section className="wiz-panel">
          <h2 className="wiz-panel__title">Media</h2>
          <p className="org-hint">
            Voleto stores media URLs rather than hosting uploads today, so paste links to
            hosted files. Landscape crops work best; the event page uses a 21:9 hero.
          </p>

          <label className="field wiz-field--wide">
            <span className="field__label">Featured image URL *</span>
            <input className="field__input" type="url" value={imageUrl}
              placeholder="https://…/hero.jpg"
              onChange={(e) => setImageUrl(e.target.value)} />
          </label>

          <label className="field wiz-field--wide">
            <span className="field__label">Hero video URL, MP4 (optional)</span>
            <input className="field__input" type="url" value={videoUrl}
              placeholder="https://…/hero.mp4"
              onChange={(e) => setVideoUrl(e.target.value)} />
          </label>

          <label className="field wiz-field--wide">
            <span className="field__label">Hero video URL, WebM (optional)</span>
            <input className="field__input" type="url" value={videoWebmUrl}
              placeholder="https://…/hero.webm"
              onChange={(e) => setVideoWebmUrl(e.target.value)} />
          </label>

          <p className="org-hint">
            The video is an extra, never a replacement. It plays silently on a loop with
            no controls, and only if it starts within about two seconds. If it is slow,
            if the visitor has reduced motion turned on, or if their browser is in data
            saver mode, the page simply keeps showing your image. Supply the MP4 if you
            supply anything; WebM is an optional second source for browsers that prefer
            it. Keep it short and quiet, it will loop forever.
          </p>

          {imageUrl.trim() && (
            <div className="wiz-preview">
              <img src={imageUrl} alt="" onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }} />
            </div>
          )}

          <div className="wiz-actions">
            <button type="button" className="button button--ghost" onClick={() => setStep(1)}>
              Back
            </button>
            <button type="button" className="button button--primary" disabled={saving}
              onClick={commitImage}>
              {saving ? 'Saving…' : 'Save & continue'}
            </button>
          </div>
        </section>
      )}

      {/* ---------------------------------------------------------------- 4 */}
      {step === 3 && eventId && (
        <section className="wiz-panel">
          <h2 className="wiz-panel__title">Review</h2>

          <dl className="wiz-review">
            <div><dt>Title</dt><dd>{basics.title || '-'}</dd></div>
            <div><dt>When</dt><dd>{basics.eventDate || '-'} {basics.eventTime}</dd></div>
            <div><dt>Doors</dt><dd>{basics.doorsTime || '-'}</dd></div>
            <div><dt>Venue</dt><dd>
              {[basics.venueName, basics.venueCity, basics.venueState].filter(Boolean).join(', ') || '-'}
            </dd></div>
            <div><dt>Age</dt><dd>{basics.ageRestriction || '-'}</dd></div>
            <div><dt>Genre</dt><dd>{basics.genre || '-'}</dd></div>
            <div><dt>Tiers</dt><dd className="mono">{tiers.length}</dd></div>
            <div><dt>Capacity</dt><dd className="mono">
              {tiers.reduce((n, t) => n + t.quantityTotal, 0)}
            </dd></div>
          </dl>

          {tiers.length === 0 && (
            <p className="org-blocked">
              <span aria-hidden="true">⚠</span> No ticket tiers yet, buyers will have
              nothing to purchase.
            </p>
          )}

          <div className="wiz-next">
            <h3>What happens next</h3>
            <ol>
              <li>Your event is saved as a <strong>draft</strong>. Nothing is on sale.</li>
              <li>
                Submit compliance documentation on the event page. Voleto reviews and
                approves each of the eight items.
              </li>
              <li>
                Once every item is cleared, the <strong>Publish</strong> action unlocks and
                tickets go on sale.
              </li>
            </ol>
          </div>

          <div className="wiz-actions">
            <button type="button" className="button button--ghost" onClick={() => setStep(2)}>
              Back
            </button>
            <Link to={paths.manage(eventId)} className="button button--primary">
              Finish & manage event
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}

/** Step 2. Each tier is committed to the engine as it is added, not batched. */
function TierStep({
  eventId, tiers, onChanged, onBack, onNext,
}: {
  eventId: string;
  tiers: ManagedTier[];
  onChanged: () => Promise<void> | void;
  onBack: () => void;
  onNext: () => void;
}) {
  const [draft, setDraft] = useState({
    name: '', description: '', price: '', quantity: '', saleStart: '', saleEnd: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add() {
    const priceCents = toCents(draft.price);
    const quantityTotal = Number.parseInt(draft.quantity, 10);

    if (!draft.name.trim()) return setError('Tier name is required.');
    if (!Number.isInteger(priceCents) || priceCents < 0) return setError('Enter a valid price.');
    if (!Number.isInteger(quantityTotal) || quantityTotal <= 0) return setError('Enter a quantity of at least 1.');

    setBusy(true);
    setError(null);
    try {
      await createTier(eventId, {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        priceCents,
        quantityTotal,
        saleStart: draft.saleStart || null,
        saleEnd: draft.saleEnd || null,
      });
      setDraft({ name: '', description: '', price: '', quantity: '', saleStart: '', saleEnd: '' });
      await onChanged();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not add that tier.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(tier: ManagedTier) {
    if (tier.quantitySold > 0) {
      setError('That tier already has sales and cannot be removed. Pause it instead.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await deleteTier(eventId, tier.id);
      await onChanged();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not remove that tier.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="wiz-panel">
      <h2 className="wiz-panel__title">Ticket tiers</h2>
      <p className="org-hint">
        Buyers pay exactly the face value you set, Voleto adds no booking or service fee
        at checkout.
      </p>

      {tiers.length > 0 && (
        <ul className="wiz-tiers">
          {tiers.map((t) => (
            <li key={t.id} className="wiz-tier">
              <div>
                <span className="wiz-tier__name">{t.name}</span>
                {t.description && <span className="wiz-tier__desc">{t.description}</span>}
              </div>
              <span className="wiz-tier__price mono">{formatMoney(t.priceCents)}</span>
              <span className="wiz-tier__qty mono">
                {t.quantityTotal} total
                {t.quantitySold > 0 && <span className="org-hint"> · {t.quantitySold} sold</span>}
              </span>
              <button
                type="button" className="button button--ghost wiz-tier__remove"
                disabled={busy || t.quantitySold > 0}
                title={t.quantitySold > 0 ? 'Has sales, cannot be removed' : 'Remove tier'}
                onClick={() => remove(t)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="wiz-add">
        <h3 className="wiz-add__title">Add a tier</h3>
        <div className="wiz-grid">
          <label className="field">
            <span className="field__label">Name *</span>
            <input className="field__input" type="text" value={draft.name} placeholder="General Admission"
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
          </label>
          <label className="field">
            <span className="field__label">Price (USD) *</span>
            <input className="field__input mono" type="number" min="0" step="0.01" value={draft.price}
              placeholder="35.00"
              onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))} />
          </label>
          <label className="field">
            <span className="field__label">Quantity *</span>
            <input className="field__input mono" type="number" min="1" value={draft.quantity}
              placeholder="200"
              onChange={(e) => setDraft((d) => ({ ...d, quantity: e.target.value }))} />
          </label>
          <label className="field wiz-field--wide">
            <span className="field__label">Description</span>
            <input className="field__input" type="text" value={draft.description}
              placeholder="Standing room, main floor."
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} />
          </label>
          <label className="field">
            <span className="field__label">Sale starts</span>
            <input className="field__input" type="datetime-local" value={draft.saleStart}
              onChange={(e) => setDraft((d) => ({ ...d, saleStart: e.target.value }))} />
          </label>
          <label className="field">
            <span className="field__label">Sale ends</span>
            <input className="field__input" type="datetime-local" value={draft.saleEnd}
              onChange={(e) => setDraft((d) => ({ ...d, saleEnd: e.target.value }))} />
          </label>
        </div>

        {error && <p className="org-error" role="alert">{error}</p>}

        <button type="button" className="button button--secondary" disabled={busy} onClick={add}>
          {busy ? 'Adding…' : 'Add tier'}
        </button>
      </div>

      <div className="wiz-actions">
        <button type="button" className="button button--ghost" onClick={onBack}>Back</button>
        <button type="button" className="button button--primary" onClick={onNext}>
          Continue
        </button>
      </div>
    </section>
  );
}
